import { eachDayOfInterval, format } from "date-fns";
import React, { useEffect, useState, useContext, useCallback } from "react";
import styled from "styled-components";

import Button from "./ui/Button";
import ButtonGroup from "./ui/ButtonGroup";
import { dateFormat, weekdays } from "../helper";
import Divider from "./ui/Divider";
import EndTimeField from "./ui/EndTimeField";
import Errors from "./ui/Errors";
import Field from "./ui/Field";
import Flatpickr from "./ui/Flatpickr";
import Flex from "./ui/Flex";
import Grid from "./ui/Grid";
import GridWrap from "./ui/GridWrap";
import GuestField from "./ui/GuestField";
import Package from "./ui/Package";
import StartTimeField from "./ui/StartTimeField";
import { tableStore } from "../store/table";
import Text from "./ui/Text";

const Tab = styled(Button)`
  padding: 20px 5px;
  background: ${(props) =>
    props.active ? (props.isChristmas && '#7f1b1b') || "var(--colour-tertiary-dmn)" : (props.isChristmas && '#7f1b1b') || "transparent" };
  border-color: ${(props) =>
    props.active ? (props.isChristmas && 'white') || "var(--colour-tertiary-dmn)" : "white"};

    @media (max-width: 500px) {
      padding: 10px 3px;
      font-size: 12px;
    }
`;

const Start = () => {
  const today = (new Date()).toISOString().split("T")[0];

  const { isActive, table, tableDispatch, setup, actions } =
    useContext(tableStore);

  const [activeOption, setActiveOption] = useState(false);
  const [options, setOptions] = useState({ categories: [] });

  const getBookingTypeValues = (id, bookingTypes) => {
    if (id && typeof bookingTypes === "object") {
      const bookingType = bookingTypes.find(({ id: bookingTypeId }) => bookingTypeId === id);

      if (bookingType) {
        const { overrides, packageMandatory, rules } = bookingType;

        return { overrides, packageMandatory, rules };
      }
    }

    return {};
  };

  const setBookingType = (data) => {
    const { overrides, packageMandatory, rules } = getBookingTypeValues(data.id, activeOption.bookingTypes);

    tableDispatch([
      { type: "SET_API_ENABLED", data: false },
      { type: "SET_BOOKING_TYPE", data },
      {
        type: "SET_MANDATORY_PACKAGES",
        data: packageMandatory
      },
      {
        type: "SET_OVERRIDES",
        data: overrides
      },
      { type: "SET_PACKAGES", data: data.packages },
      {
        type: "SET_RULES",
        data: rules
      }
    ]);
  };

  const load = useCallback(() => {
    fetch(`${setup.endpoint}/v1/venue/${setup.venueId}/setup`, {
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data?.categories) setOptions(data.categories);
        if (data?.categories?.length) {
          setActiveOption(data.categories[0]);

        //   if (data?.categories[0]?.bookingTypes?.length) {
        //     setBookingType(data?.categories[0]?.bookingTypes[0]);
        //   }
        }
      });
  }, [setBookingType, setup.endpoint, setup.venueId, table]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    tableDispatch({
      type: "SET_PACKAGES",
      data: activeOption.packages,
    });
  }, [activeOption, tableDispatch]);

  const getAlertText = (id, option) => {
    if (id && typeof option.bookingTypes === "object") {
      const bookingType = option.bookingTypes.find(({ id: bookingTypeId }) => bookingTypeId === id);

      if (bookingType) {
        if (((!bookingType.alertText || bookingType.alertText === null) && !option.title.toLowerCase().includes("christmas") && bookingType.showDefaultAlertText)) {
          return "Please note we keep an allocation of tables available for walk ins on the day so do pop by and we will always try and accommodate you.";
        }

        return bookingType.alertText;
      }
    }

    return "";
  }

  const alertText = getAlertText(table.bookingType, activeOption);

  const getEnabledDates = (overrides, rules) => {
    const alwaysEnabled = new Set();
    const alwaysDisabled = new Set();
    
    overrides.forEach((override) => {
      const { available } = override;

      const overrideDates = eachDayOfInterval({ start: new Date(`${override.dateFrom}T00:00:00`), end: new Date(`${override.dateTo}T23:59:59`) });

      if (available) {
        overrideDates.forEach((date) => alwaysEnabled.add(format(date, "yyyy-MM-dd")));
      } else {
        overrideDates.forEach((date) => alwaysDisabled.add(format(date, "yyyy-MM-dd")));
      }
    });

    const weekdayIndexes = [];

    Object.values(rules).forEach((rule) => {
      const { available, dayOfWeek } = rule;

      if (available && dayOfWeek) {
        const weekdayIndex = weekdays.findIndex((weekday) => weekday === dayOfWeek);

        if (weekdayIndex > -1) {
          weekdayIndexes.push(weekdayIndex);
        }
      }
    });
    
    return [
      (date) => {
        const dateStr = format(date, "yyyy-MM-dd");

        if (!alwaysDisabled.has(dateStr) && (alwaysEnabled.has(dateStr) || weekdayIndexes.includes(date.getDay()))) {
          return true;
        }

        return false;
      },
    ];
  };

  useEffect(() => {
    if (!isActive || !table.date) return;
    actions.sendAnalyticsData({
      hitType: "event",
      eventAction: "Selection",
      eventLabel: `Date - ${table.date}`,
    });

  }, [isActive, table.date]);

  const handleCategoryChange = (option) => {
    actions.sendAnalyticsData({
      hitType: "event",
      eventAction: "Selection",
      eventLabel: `Category - ${option.title}`,
    });

    setActiveOption(option);
  }

  const handleDateChange = (date) => {
    tableDispatch([
      { type: "SET_API_ENABLED", data: true },
      ...(typeof date !== "undefined" ? [{ type: "SET_DATE", data: date }] : []),
    ]);
  };

  const handleGuestsChange = ({ guests, nonInterraction = false }) => {
    if (!nonInterraction) {
      actions.sendAnalyticsData({
        hitType: "event",
        eventAction: "Selection",
        eventLabel: `Guests - ${guests}`,
      });
    }

    tableDispatch({
      type: "SET_GUESTS",
      data: guests,
    });
  };

  const handleTimeStartChange = ({ time, nonInterraction = false }) => {
    if (!nonInterraction) {
      actions.sendAnalyticsData({
        hitType: "event",
        eventAction: "Selection",
        eventLabel: `Start Time - ${time}`,
      });
    }

    tableDispatch({
      type: "SET_TIME",
      data: time,
    });
  };

  const handleDurationChange = ({ duration, nonInterraction = false }) => {
    if (!nonInterraction) {
      const [h, m] = table.time.split(":");
      const totalHours = Number(h) + Math.floor(duration / 60);
      const totalMinutes = Number(m) + (Number(duration) % 60);

      actions.sendAnalyticsData({
        hitType: "event",
        eventAction: "Selection",
        eventLabel: `End Time - ${totalHours
          .toString()
          .padStart(2, "0")}:${totalMinutes.toString().padStart(2, "0")}`,
      });
    }

    tableDispatch({
      type: "SET_DURATION",
      data: duration,
    });
  };

  const handleGetAvailability = () => {
    actions.sendAnalyticsData({
      hitType: "event",
      eventAction: "Submit",
      eventLabel: "Check Availability",
    });
    actions.checkAvailability();
  };

  return (
    <>
      <Flex>
        {options &&
          Array.from(options, (option) => (
            <Tab
              key={option.title}
              active={activeOption.title === option.title}
              onClick={() => handleCategoryChange(option)}
              isChristmas={option.title === "Christmas"}
            >
              {option.title}
            </Tab>
          ))}
      </Flex>
      {activeOption && activeOption.message && (
        <Text>
          {activeOption.message}
        </Text>
      )}
      <Divider />
      <GridWrap>
        {activeOption &&
          activeOption.bookingTypes &&
          Array.from(activeOption.bookingTypes, (data) => (
            <Package
              key={data.id}
              data={data}
              click={setBookingType}
              active={table.bookingType === data.id}
            />
          ))}
      </GridWrap>
      <Divider />
      {alertText && (
        <Text>
          {alertText}
        </Text>
      )}
      <Grid>
        <div>
          <Field label="Date" value={(dateFormat(table.date) ? dateFormat(table.date) : 'Select Date')}>
            <Flatpickr defaultDate={table.date} enable={getEnabledDates(table.overrides, table.rules)} handleChange={handleDateChange} minDate={today} redraw={table.bookingType}>
              <input type="text" />
            </Flatpickr>
          </Field>
        </div>
        <div>
          <GuestField
            date={table.date}
            overrides={table.overrides}
            rules={table.rules}
            handleGuestsChange={handleGuestsChange}
            value={table.numPeople}
          />
        </div>
        <div>
          <StartTimeField
            apiEnabled={table.apiEnabled}
            bookingTypeId={table.bookingType}
            date={table.date}
            endpoint={setup.endpoint}
            numPeople={table.numPeople}
            rules={table.rules}
            tableDispatch={tableDispatch}
            timeslots={table.timeslots}
            value={table.time}
            venueId={setup.venueId}
            handleTimeStartChange={handleTimeStartChange}
          />
        </div>
        <div>
          <EndTimeField
            date={table.date}
            endTimeslots={table.endTimeslots}
            maxDuration={table.maxDuration}
            minDuration={table.minDuration}
            tableDispatch={tableDispatch}
            time={table.time}
            value={table.duration}
            handleDurationChange={handleDurationChange}
          />
        </div>
      </Grid>
      <Errors errors={table.errors} />
      <ButtonGroup>
        <Button
          active
          disabled={
            table.loading
            || !table.duration
            || !table.time
            || !table.date
            || !table.bookingType
          }
          onClick={handleGetAvailability}
        >
          Check Availability
        </Button>
      </ButtonGroup>
    </>
  );
};

export default Start;
